import {
  CellClassParams,
  ColDef,
  EditableCallbackParams,
  FilterChangedEvent,
  ValueFormatterParams,
} from 'ag-grid-community'
import { CreatorEarnings, CreatorEarningsOverview } from 'shared/src/api/types'
import { formatDecimalUsd } from 'shared/src/utils/formatting'

export const EDITABLE_CELL_BACKGROUND_COLOR = '#BDEFFC'
export const EDITABLE_PARTNERSHIP_CELL_BACKGROUND_COLOR = '#EFB0FF'
export const EDITED_CELL_BACKGROUND_COLOR = '#FAC79B'

export const DOLLAR_AMOUNT_COLUMN_WIDTH = 90

type EarningsType =
  | 'mediaViews'
  | 'productViews'
  | 'outgoingProductClicks'
  | 'mediaPublished'
  | 'mediaTagged'
  | 'editsCreated'

const EARNINGS_COLUMNS: Record<EarningsType, string> = {
  mediaViews: 'mediaViewEarnings',
  productViews: 'productViewEarnings',
  outgoingProductClicks: 'outgoingProductClickEarnings',
  mediaPublished: 'mediaPublishedEarnings',
  mediaTagged: 'mediaTaggedEarnings',
  editsCreated: 'editsCreatedEarnings',
}

const UPDATED_EARNINGS_COLUMNS: Record<EarningsType, string> = {
  mediaViews: 'updatedMediaViewEarnings',
  productViews: 'updatedProductViewEarnings',
  outgoingProductClicks: 'updatedOutgoingProductClickEarnings',
  mediaPublished: 'updatedMediaPublishedEarnings',
  mediaTagged: 'updatedMediaTaggedEarnings',
  editsCreated: 'updatedEditsCreatedEarnings',
}

export const NUMBER_COLUMN: ColDef = {
  filter: 'agNumberColumnFilter',
  headerClass: 'ag-right-aligned-header',
  cellClass: 'ag-right-aligned-cell',
  width: 120,
  valueParser: (params) => Number(params.newValue),
}

export const DEFAULT_COL_DEF: ColDef = {
  filter: 'agTextColumnFilter',
  sortable: true,
  resizable: true,
  width: 120,
}

export const COLUMN_TYPES = {
  number: NUMBER_COLUMN,
}

export const getIsEditable = (params: EditableCallbackParams) =>
  !(params.data.isCommitted || params.node.isRowPinned())

export const formatPrice = (params: ValueFormatterParams) =>
  params.value ? `$${formatDecimalUsd(params.value)}` : ''

export const formatNumber = (params: ValueFormatterParams) =>
  params.value.toLocaleString('en-US')

export const overviewTotalEarnings = (earnings: CreatorEarningsOverview) =>
  earnings.mediaViewEarnings +
  earnings.productViewEarnings +
  earnings.outgoingProductClickEarnings +
  earnings.mediaPublishedEarnings +
  earnings.mediaTaggedEarnings +
  earnings.editsCreatedEarnings +
  earnings.bonusEarnings +
  earnings.partnershipEarnings

export const totalEarnings = (earnings: CreatorEarnings) =>
  (earnings.updatedMediaViewEarnings ?? earnings.mediaViewEarnings) +
  (earnings.updatedProductViewEarnings ?? earnings.productViewEarnings) +
  (earnings.updatedOutgoingProductClickEarnings ??
    earnings.outgoingProductClickEarnings) +
  (earnings.updatedMediaPublishedEarnings ??
    earnings.mediaPublishedEarnings ??
    0) +
  (earnings.updatedMediaTaggedEarnings ?? earnings.mediaTaggedEarnings ?? 0) +
  (earnings.updatedEditsCreatedEarnings ?? earnings.editsCreatedEarnings ?? 0) +
  (earnings.bonusEarnings ?? 0) +
  (earnings.partnershipEarnings ?? 0)

export const displayedEarnings = (
  earnings: CreatorEarnings,
  type: EarningsType
) => {
  let earningsKey = EARNINGS_COLUMNS[type] as keyof CreatorEarnings
  let updateEarningsKey = UPDATED_EARNINGS_COLUMNS[
    type
  ] as keyof CreatorEarnings
  return earnings[updateEarningsKey] ?? earnings[earningsKey]
}

export const getAggregateRow = (earnings: CreatorEarnings[]) => [
  {
    mediaViews: earnings
      .map((earning) => earning.mediaViews)
      .reduce((prev, curr) => prev + curr, 0),
    mediaViewEarnings: earnings
      .map(
        (earning) =>
          earning.updatedMediaViewEarnings ?? earning.mediaViewEarnings
      )
      .reduce((prev, curr) => prev + curr, 0),
    productViews: earnings
      .map((earning) => earning.productViews)
      .reduce((prev, curr) => prev + curr, 0),
    productViewEarnings: earnings
      .map(
        (earning) =>
          earning.updatedProductViewEarnings ?? earning.productViewEarnings
      )
      .reduce((prev, curr) => prev + curr, 0),
    outgoingProductClicks: earnings
      .map((earning) => earning.outgoingProductClicks)
      .reduce((prev, curr) => prev + curr, 0),
    outgoingProductClickEarnings: earnings
      .map(
        (earning) =>
          earning.updatedOutgoingProductClickEarnings ??
          earning.outgoingProductClickEarnings
      )
      .reduce((prev, curr) => prev + curr, 0),
    mediaPublished: earnings
      .map((earning) => earning.mediaPublished)
      .reduce((prev, curr) => prev + curr, 0),
    mediaPublishedEarnings: earnings
      .map(
        (earning) =>
          earning.updatedMediaPublishedEarnings ??
          earning.mediaPublishedEarnings
      )
      .reduce((prev, curr) => prev + curr, 0),
    mediaTagged: earnings
      .map((earning) => earning.mediaTagged)
      .reduce((prev, curr) => prev + curr, 0),
    mediaTaggedEarnings: earnings
      .map(
        (earning) =>
          earning.updatedMediaTaggedEarnings ?? earning.mediaTaggedEarnings
      )
      .reduce((prev, curr) => prev + curr, 0),
    editsCreated: earnings
      .map((earning) => earning.editsCreated)
      .reduce((prev, curr) => prev + curr, 0),
    editsCreatedEarnings: earnings
      .map(
        (earning) =>
          earning.updatedEditsCreatedEarnings ?? earning.editsCreatedEarnings
      )
      .reduce((prev, curr) => prev + curr, 0),
    bonusEarnings: earnings
      .map((earning) => earning.bonusEarnings)
      .reduce((prev, curr) => (prev ?? 0) + (curr ?? 0), 0),
    partnershipEarnings: earnings
      .map((earning) => earning.partnershipEarnings)
      .reduce((prev, curr) => (prev ?? 0) + (curr ?? 0), 0),
  },
]

export const getOverviewAggregateRow = (
  earnings: CreatorEarningsOverview[]
) => [
  {
    mediaViews: earnings
      .map((earning) => earning.mediaViews)
      .reduce((prev, curr) => prev + curr, 0),
    mediaViewEarnings: earnings
      .map((earning) => earning.mediaViewEarnings)
      .reduce((prev, curr) => prev + curr, 0),
    productViews: earnings
      .map((earning) => earning.productViews)
      .reduce((prev, curr) => prev + curr, 0),
    productViewEarnings: earnings
      .map((earning) => earning.productViewEarnings)
      .reduce((prev, curr) => prev + curr, 0),
    outgoingProductClicks: earnings
      .map((earning) => earning.outgoingProductClicks)
      .reduce((prev, curr) => prev + curr, 0),
    outgoingProductClickEarnings: earnings
      .map((earning) => earning.outgoingProductClickEarnings)
      .reduce((prev, curr) => prev + curr, 0),
    mediaPublished: earnings
      .map((earning) => earning.mediaPublished)
      .reduce((prev, curr) => prev + curr, 0),
    mediaPublishedEarnings: earnings
      .map((earning) => earning.mediaPublishedEarnings)
      .reduce((prev, curr) => prev + curr, 0),
    mediaTagged: earnings
      .map((earning) => earning.mediaTagged)
      .reduce((prev, curr) => prev + curr, 0),
    mediaTaggedEarnings: earnings
      .map((earning) => earning.mediaTaggedEarnings)
      .reduce((prev, curr) => prev + curr, 0),
    editsCreated: earnings
      .map((earning) => earning.editsCreated)
      .reduce((prev, curr) => prev + curr, 0),
    editsCreatedEarnings: earnings
      .map((earning) => earning.editsCreatedEarnings)
      .reduce((prev, curr) => prev + curr, 0),
    bonusEarnings: earnings
      .map((earning) => earning.bonusEarnings)
      .reduce((prev, curr) => prev + curr, 0),
    partnershipEarnings: earnings
      .map((earning) => earning.partnershipEarnings)
      .reduce((prev, curr) => prev + curr, 0),
  },
]

export const onFilterChanged = (
  event: FilterChangedEvent,
  creatorEarnings: CreatorEarnings[]
) => {
  const ids: number[] = []
  event.api.forEachNodeAfterFilter((node) => ids.push(node.data.id))

  event.api.setPinnedTopRowData(
    getAggregateRow(creatorEarnings.filter((e) => ids.includes(e.id)))
  )
}

export const getEditableCellStyle = (
  params: CellClassParams,
  type: EarningsType
) => {
  if (params.node.isRowPinned()) return null

  let earnings = params.data
  let earningsKey = EARNINGS_COLUMNS[type] as keyof CreatorEarnings
  let updateEarningsKey = UPDATED_EARNINGS_COLUMNS[
    type
  ] as keyof CreatorEarnings

  return !!earnings[updateEarningsKey] &&
    earnings[updateEarningsKey] !== earnings[earningsKey]
    ? { backgroundColor: EDITED_CELL_BACKGROUND_COLOR }
    : earnings['isCommitted']
    ? null
    : { backgroundColor: EDITABLE_CELL_BACKGROUND_COLOR }
}

export const dateComparator = (filterDate: Date, cellValue: string | null) => {
  if (!cellValue) {
    return 0
  }
  const dateParts = cellValue.split('-')
  const yearNumber = Number(dateParts[0])
  const monthNumber = Number(dateParts[1]) - 1
  const dayNumber = Number(dateParts[2])
  const cellDate = new Date(yearNumber, monthNumber, dayNumber)
  if (cellDate < filterDate) {
    return -1
  } else if (cellDate > filterDate) {
    return 1
  }
  return 0
}
